import {
  rotuloDaAcaoAuditada,
  rotuloDaEntidadeAuditada,
  type TrilhaDeAuditoria as Trilha,
} from '@reducee/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { BotaoDePagina, CabecalhoDeTabela, CLASSE_TABELA } from './Superficie.tsx';
import { chamarApi } from '../api/cliente.ts';
import { CampoSelecao } from './Campo.tsx';

/**
 * Trilha de auditoria.
 *
 * Só leitura: nenhuma tela da aplicação edita ou apaga um registro. Responde
 * quem fez o quê e quando.
 *
 * Mora em componente porque aparece em dois lugares: embutida no acordeão de
 * Configurações, que é por onde a operação chega, e na rota `/auditoria`.
 */
export function TrilhaDeAuditoria(): React.JSX.Element {
  const [entidade, setEntidade] = useState('');
  const [pagina, setPagina] = useState(1);

  const consulta = useQuery({
    queryKey: ['auditoria', entidade, pagina],
    queryFn: () =>
      chamarApi<Trilha>(
        `/auditoria?pagina=${pagina}${entidade === '' ? '' : `&entidade=${entidade}`}`,
      ),
    // Sem isto a troca de página pisca a tabela vazia entre uma busca e outra,
    // e a altura do cartão pula junto.
    placeholderData: (anterior) => anterior,
  });

  const trilha = consulta.data;
  const paginas = trilha ? Math.max(1, Math.ceil(trilha.total / trilha.porPagina)) : 1;

  return (
    <div>
      <p className="text-sm text-texto-suave">
        Quem fez o quê e quando. O registro não é editável por nenhuma tela.
      </p>

      <div className="mt-4 max-w-xs">
        <CampoSelecao
          rotulo="Entidade"
          name="entidade"
          value={entidade}
          onChange={(evento) => {
            setEntidade(evento.target.value);
            // Voltar à primeira: a página 4 do filtro anterior quase nunca
            // existe no novo, e cair numa tabela vazia parece defeito.
            setPagina(1);
          }}
        >
          <option value="">Todas</option>
          {(trilha?.entidades ?? []).map((nome) => (
            // O valor continua sendo o nome do modelo, que é o que a rota
            // filtra; só o rótulo fala a língua da operação. "Implantacao" e
            // "RegraComissao" são como o código audita, não como a
            // Administradora chama as coisas.
            <option key={nome} value={nome}>
              {rotuloDaEntidadeAuditada(nome)}
            </option>
          ))}
        </CampoSelecao>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl bg-superficie ring-1 ring-linha">
        <table className={CLASSE_TABELA}>
          <CabecalhoDeTabela colunas={['Quando', 'Usuária', 'Ação', 'Entidade', 'Detalhes']} />
          <tbody>
            {trilha?.registros.map((registro) => (
              <tr
                key={registro.id}
                data-teste="registro-auditoria"
                className="border-b border-linha last:border-0 align-top"
              >
                <td className="px-3.25 py-3 whitespace-nowrap">
                  {new Date(registro.criadoEm).toLocaleString('pt-BR')}
                </td>
                <td className="px-3.25 py-3">{registro.usuaria?.nome ?? 'Sistema'}</td>
                <td className="px-3.25 py-3 font-medium">
                  {rotuloDaAcaoAuditada(registro.acao)}
                </td>
                <td className="px-3.25 py-3">{rotuloDaEntidadeAuditada(registro.entidade)}</td>
                <td className="px-3.25 py-3 text-xs text-texto-suave">
                  {registro.detalhes ? (
                    <code className="break-all">{JSON.stringify(registro.detalhes)}</code>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {consulta.isLoading ? <p className="p-4 text-texto-suave">Carregando…</p> : null}
        {trilha?.registros.length === 0 ? (
          <p className="p-4 text-texto-suave">Nenhum registro neste filtro.</p>
        ) : null}
      </div>

      {/* O rodapé aparece sempre que houver registro, mesmo com uma página só.
          Esconder o total quando cabe tudo numa página pouparia uma linha e
          custaria a única resposta que a trilha precisa dar de saída: quantos
          registros existem. Os botões ficam, desabilitados, para a posição não
          mudar de lugar ao virar a página. */}
      {trilha && trilha.total > 0 ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-texto-suave" data-teste="posicao-da-trilha">
            Página {trilha.pagina} de {paginas} · {trilha.total}{' '}
            {trilha.total === 1 ? 'registro' : 'registros'}
          </p>
          <div className="flex gap-2">
            <BotaoDePagina
              rotulo="Anterior"
              desabilitado={trilha.pagina <= 1}
              aoClicar={() => setPagina((atual) => Math.max(1, atual - 1))}
            />
            <BotaoDePagina
              rotulo="Próxima"
              desabilitado={trilha.pagina >= paginas}
              aoClicar={() => setPagina((atual) => Math.min(paginas, atual + 1))}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
